import ApiModel              from '@widesk-core/models/ApiModel';
import BatchOperation        from '@widesk-core/models/batch/BatchOperation';
import { BatchOperationOptions } from '@widesk-core/models/batch/BatchOperation';
import BatchError            from '@widesk-core/models/batch/BatchError';
import { BatchConnector }    from '@widesk-core/models/batch/types';
import { BatchMethod }       from '@widesk-core/models/batch/types';
import { runInAction }       from 'mobx';

/**
 * Ce qu'une opération peut viser : un model (déjà enregistré ou non), ou la poignée d'une opération
 * antérieure du MÊME lot, pour enchaîner sur un objet que le lot n'a pas encore créé.
 */
export type BatchTarget<M extends ApiModel> = M | BatchOperation<M>;

export type BatchWriteOptions = {
	/** Les paramètres de la route (`POST /features?map=7`), quand le service en attend. */
	params?: Record<string, unknown>;
};

export type BatchManagerOptions = {
	/**
	 * `false` pour jouer le lot sans transaction englobante (`?_transactional=0`) : chaque requête
	 * est alors indépendante, celles qui passent sont conservées même si une autre échoue.
	 */
	transactional?: boolean;
};

/**
 * Regroupe plusieurs écritures en UN appel à `POST /_batch`, pour bénéficier de la transaction que
 * l'API sait faire et que le front ne sait pas : à la première réponse non 2xx, tout est annulé.
 *
 * L'intérêt est de garder les models : on écrit le lot comme on écrivait les appels unitaires
 * (`batch.patch(model, attrs)` pour `model.patch(attrs)`, `batch.destroy(model)` pour
 * `model.destroy()`, `batch.createMany`/`batch.patchMany`/`batch.destroyWhere` pour les écritures
 * de collection, `await batch.run()` pour le `await`), et les models passés sont hydratés avec ce que
 * l'API renvoie — le reste de l'application ne voit pas la différence.
 *
 * ```ts
 * const batch = new BatchManager();
 *
 * const place = batch.create(PlaceModel, { label, project: projectUrn });
 *
 * // `place.urn` est ici `$:map:place:[:$0.id]` : l'API le remplacera par l'urn réellement créée.
 * fields.forEach(field => batch.create(PlaceFieldModel, { place: place.urn, field: field.urn, value: '…' }));
 *
 * await batch.run();
 *
 * place.model.id; // l'id réel
 * ```
 *
 * Limite du jour : la route n'existe que dans le service `map`, donc toutes les opérations d'un lot
 * doivent viser ce service (un lot mélangeant deux services est refusé avant l'envoi).
 */
export default class BatchManager {
	private readonly _operations: BatchOperation<any>[] = [];
	private readonly _transactional: boolean;
	private _hasRun = false;

	public constructor(options: BatchManagerOptions = {}) {
		this._transactional = options.transactional !== false;
	}

	public get operations(): readonly BatchOperation<any>[] {
		return this._operations;
	}

	public get length() {
		return this._operations.length;
	}

	public get isEmpty() {
		return !this._operations.length;
	}

	/** Équivalent de `new ModelClass().patch(attributes)` : une création. */
	public create<M extends ApiModel>(
		ModelClass: new (...args: any[]) => M,
		attributes: Record<string, unknown>,
		options: BatchWriteOptions = {},
	): BatchOperation<M> {
		return this.patch(new ModelClass(), attributes, options);
	}

	/**
	 * Équivalent de `model.patch(attributes)`, avec la même règle : un POST si la cible n'a pas
	 * encore d'id, un PATCH sinon.
	 */
	public patch<M extends ApiModel>(
		target: BatchTarget<M>,
		attributes: Record<string, unknown>,
		options: BatchWriteOptions = {},
	): BatchOperation<M> {
		const { model, id } = this._resolve(target);

		return this._add(model, id ? 'PATCH' : 'POST', { targetId: id, attributes, params: options.params });
	}

	/** Équivalent de `model.save()` : envoie les attributs portés par le model. */
	public save<M extends ApiModel>(target: BatchTarget<M>): BatchOperation<M> {
		const { model, id } = this._resolve(target);

		return this._add(model, id ? 'PATCH' : 'POST', {
			targetId: id,
			attributes: model.untransformedAttributes as Record<string, unknown>,
		});
	}

	/**
	 * Équivalent d'`ApiCollection.batch({ method: 'post' })` : UNE requête sur la route de collection
	 * (`POST /features/batch`), corps à plat. Rien n'est hydraté en retour, donc pas de jeton
	 * `[:$N.id]` sur les objets créés.
	 */
	public createMany<M extends ApiModel>(
		ModelClass: new (...args: any[]) => M,
		items: Record<string, unknown>[],
		options: BatchWriteOptions = {},
	): BatchOperation<M> {
		if (!items.length) {
			throw new Error('BatchManager : une création en lot sans objet à créer.');
		}

		return this._add(new ModelClass(), 'POST', {
			attributes: items,
			params: options.params,
			pathSuffix: 'batch',
			hydrate: false,
		});
	}

	/**
	 * Équivalent d'`ApiCollection.save()` en modification : UNE requête sur le path de la collection
	 * (`PATCH /maps`), corps enveloppé dans `items`, chaque item portant l'`id` qu'il vise — le
	 * suffixe `batch` ne concerne que la création (voir `ApiCollection.save`).
	 */
	public patchMany<M extends ApiModel>(
		ModelClass: new (...args: any[]) => M,
		items: Record<string, unknown>[],
		options: BatchWriteOptions = {},
	): BatchOperation<M> {
		if (!items.length) {
			throw new Error('BatchManager : une modification en lot sans objet à modifier.');
		}

		return this._add(new ModelClass(), 'PATCH', {
			attributes: { items },
			params: options.params,
			hydrate: false,
		});
	}

	/**
	 * Équivalent d'`ApiCollection.destroy()` : UNE requête qui supprime ce que matchent les filtres
	 * (`DELETE /features?id[]=1&id[]=2`), obligatoires — sans eux la route viserait la collection
	 * entière. Aucun model n'est marqué détruit, la ressource est purgée du cache.
	 */
	public destroyWhere<M extends ApiModel>(
		ModelClass: new (...args: any[]) => M,
		filters: Record<string, unknown>,
	): BatchOperation<M> {
		if (!Object.keys(filters).length) {
			throw new Error('BatchManager : une suppression par filtres en exige au moins un.');
		}

		return this._add(new ModelClass(), 'DELETE', { params: filters });
	}

	/** Équivalent de `model.destroy()`. */
	public destroy<M extends ApiModel>(target: BatchTarget<M>): BatchOperation<M> {
		const { model, id } = this._resolve(target);

		if (!id) {
			throw new Error(`BatchManager : impossible de supprimer un <${model.modelName}> sans id.`);
		}

		return this._add(model, 'DELETE', { targetId: id });
	}

	/**
	 * Joue le lot. Rend les opérations dans l'ordre d'ajout, chacune portant sa réponse et son model
	 * à jour. Jette dès qu'une opération a échoué — comme `model.patch()` le ferait — l'erreur
	 * portant alors le détail de chaque échec (voir `BatchError`).
	 */
	public async run(): Promise<BatchOperation<any>[]> {
		if (this._hasRun) {
			throw new Error('BatchManager : ce lot a déjà été joué, construisez-en un nouveau.');
		}

		if (this.isEmpty) return [];

		const connector = this._getConnector();
		const items = this._operations.map(operation => (
			operation.toRequestItem(connector.servicePath, connector.subRequestHeaders)
		));

		// Un échec peut ressortir de deux façons. En erreur HTTP : elle traverse ici, sans qu'un seul
		// model ait été touché, et c'est au connector du projet de la signaler comme les autres. Ou
		// en 207, traité juste en dessous.
		const responses = await connector.batch(items, { transactional: this._transactional });

		this._hasRun = true;

		this._operations.forEach((operation, index) => operation.setResponse(responses[index]));

		const failed = this._operations.filter(operation => !operation.isSuccess);

		// On arrive ici sans erreur HTTP : l'API répond 207 dès qu'une requête a échoué. En lot
		// TRANSACTIONNEL, cet échec a annulé l'ensemble des écritures — y compris celles que la
		// réponse donne en 2xx : elles ont bien été jouées, puis défaites. Hydrater leurs models les
		// ferait mentir jusqu'au prochain chargement, on n'applique donc rien du tout. Sans
		// transaction, chaque requête est indépendante : celles qui sont passées sont acquises.
		if (!failed.length || !this._transactional) {
			runInAction(() => {
				this._operations.forEach(operation => operation.applyToModel());
			});
		}

		// Levée sans être affichée : dans le core, montrer une erreur n'est pas notre affaire — les
		// appelants l'attrapent déjà pour la signaler comme celle d'un `patch()` unitaire.
		if (failed.length) throw new BatchError(failed, this._operations);

		return this._operations;
	}

	private _add<M extends ApiModel>(
		model: M,
		method: BatchMethod,
		options: Omit<BatchOperationOptions<M>, 'index' | 'model' | 'method'> = {},
	): BatchOperation<M> {
		const operation = new BatchOperation<M>({
			index: this._operations.length,
			model,
			method,
			...options,
		});

		this._operations.push(operation);

		return operation;
	}

	/**
	 * Une cible peut être un model — son id est alors connu, ou absent s'il n'est pas encore créé —
	 * ou la poignée d'une opération antérieure, dont l'`id` rend le jeton `[:$N.id]` que l'API
	 * remplacera par l'id réellement créé.
	 */
	private _resolve<M extends ApiModel>(target: BatchTarget<M>): { model: M; id: id | undefined } {
		const isOperation = target instanceof BatchOperation;
		const model = isOperation ? target.model : target;
		const id = isOperation ? target.id : model.id;

		return { model, id: id || undefined };
	}

	/**
	 * Toutes les requêtes d'un lot sont rejouées par le service qui expose `/_batch` : elles passent
	 * donc par le même connector, dont on tire aussi le préfixe des routes.
	 *
	 * Reconnu à ses membres et non à sa classe : les connectors sont définis par les projets, le core
	 * n'en connaît aucun (voir `BatchConnector`).
	 */
	private _getConnector(): BatchConnector {
		const connectors = new Set(this._operations.map(operation => operation.model.connector));

		if (connectors.size > 1) {
			throw new Error('BatchManager : toutes les opérations d\'un lot doivent viser le même service.');
		}

		const connector = this._operations[0]?.model.connector as unknown as Partial<BatchConnector>;

		if (typeof connector?.batch !== 'function') {
			throw new Error('BatchManager : ce service n\'expose pas de route batch.');
		}

		return connector as BatchConnector;
	}
}
